import { produce } from 'immer'
import {
  REMOVE_ORGANIZATION_INVITE,
  RemoveOrganizationInviteActions,
} from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.actions'
import {
  ABANDON_ACCEPT_ORGANIZATION_INVITE,
  GET_ORGANIZATION_INVITE_SUCCESS,
  GET_ORGANIZATION_INVITES_FAILURE,
  GET_ORGANIZATION_INVITES_INIT,
  GET_ORGANIZATION_INVITES_SUCCESS,
  GET_USER_ORGANIZATION_INVITES_FAILURE,
  GET_USER_ORGANIZATION_INVITES_INIT,
  GET_USER_ORGANIZATION_INVITES_SUCCESS,
  GetOrganizationInviteActions,
  GetOrganizationInvitesActions,
  GetUserOrganizationInvitesActions,
  INVITE_ORGANIZATION_FAILURE,
  INVITE_ORGANIZATION_INIT,
  INVITE_ORGANIZATION_SUCCESS,
  InviteOrganizationActions,
  PUT_ORGANIZATION_INVITE_FAILURE,
  PUT_ORGANIZATION_INVITE_INIT,
  PUT_ORGANIZATION_INVITE_SUCCESS,
  PutOrganizationInviteActions,
} from 'src/redux/actions/data/organizations.actions'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface OrganizationInvitesState {
  ids: string[]
  postingOrganizationInvites: boolean
  complete: boolean
  postInvitesTopLevelError?: string
  postInvitesErrorDetailMap?: ErrorDetailMap<string>
  patchingOrganizationInvite: boolean
  patchingOrganizationInviteTopLevelError?: string
  patchingOrganizationInviteErrorDetailMap?: ErrorDetailMap<string>
  patchComplete: boolean
  gettingOrganizationInvites: boolean
  fetchingInvites: boolean
}

export const initialState: OrganizationInvitesState = {
  ids: [],
  postingOrganizationInvites: false,
  complete: false,
  patchingOrganizationInvite: false,
  patchComplete: false,
  gettingOrganizationInvites: false,
  fetchingInvites: false,
}

export type Actions =
  | InviteOrganizationActions
  | GetUserOrganizationInvitesActions
  | PutOrganizationInviteActions
  | GetOrganizationInviteActions
  | GetOrganizationInvitesActions
  | RemoveOrganizationInviteActions

const OrganizationInvitesReducer = (
  state: OrganizationInvitesState = initialState,
  action: Actions,
): OrganizationInvitesState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case INVITE_ORGANIZATION_INIT: {
        draft.postInvitesTopLevelError = undefined
        draft.postingOrganizationInvites = true
        return draft
      }

      case INVITE_ORGANIZATION_SUCCESS: {
        draft.postingOrganizationInvites = false
        draft.complete = true
        return draft
      }

      case INVITE_ORGANIZATION_FAILURE: {
        const errors = parseBackendErrors(action.payload)

        draft.postingOrganizationInvites = false
        draft.complete = true
        draft.postInvitesTopLevelError = errors.topLevelError
        draft.postInvitesErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case GET_ORGANIZATION_INVITE_SUCCESS: {
        draft.ids = Array.from(
          new Set([...draft.ids, action.payload.organizationInvite.id]),
        )
        return draft
      }

      case GET_ORGANIZATION_INVITES_INIT: {
        draft.gettingOrganizationInvites = true
        return draft
      }

      case GET_ORGANIZATION_INVITES_SUCCESS: {
        draft.ids =
          action.payload.pagination.page === 0
            ? action.payload.organizationInvites.map(
                (orgInvite) => orgInvite.id,
              )
            : [
                ...draft.ids,
                ...action.payload.organizationInvites.map(
                  (orgInvite) => orgInvite.id,
                ),
              ]
        draft.gettingOrganizationInvites = false
        return draft
      }

      case GET_ORGANIZATION_INVITES_FAILURE: {
        draft.gettingOrganizationInvites = false
        return draft
      }

      case REMOVE_ORGANIZATION_INVITE: {
        draft.ids = draft.ids.filter((id) => id !== action.payload)
        return draft
      }

      case GET_USER_ORGANIZATION_INVITES_INIT: {
        draft.fetchingInvites = true
        return draft
      }

      case GET_USER_ORGANIZATION_INVITES_SUCCESS: {
        draft.ids = action.payload.organizationInvites.map(
          (invite) => invite.id,
        )
        draft.fetchingInvites = false
        return draft
      }

      case GET_USER_ORGANIZATION_INVITES_FAILURE: {
        draft.fetchingInvites = false
        return draft
      }

      case PUT_ORGANIZATION_INVITE_INIT: {
        draft.patchingOrganizationInvite = true
        return draft
      }

      case PUT_ORGANIZATION_INVITE_SUCCESS: {
        draft.patchingOrganizationInvite = false
        draft.patchComplete = true
        return draft
      }

      case PUT_ORGANIZATION_INVITE_FAILURE: {
        const errors = parseBackendErrors(action.payload)

        draft.patchingOrganizationInvite = false
        draft.patchingOrganizationInviteTopLevelError = errors.topLevelError
        draft.patchingOrganizationInviteErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case ABANDON_ACCEPT_ORGANIZATION_INVITE: {
        draft.patchingOrganizationInviteTopLevelError = undefined
        draft.patchingOrganizationInviteErrorDetailMap = undefined
      }
    }
  })
}

export default OrganizationInvitesReducer
